<template>
    <div class="mx-2 mx-lg-5">
        <div class="card mt-3">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h3>{{ $t("headers.vehicles") }}</h3>
                <router-link
                    v-if="$store.getters.hasGatehousePerm('add_vehicleentry')"
                    :to="{ name: 'vehicleEntryAddView' }"
                    class="btn btn-sm btn-success me-3 d-inline-flex align-items-center"
                >
                    <icon icon="Plus" class="me-2"></icon>
                    <span class="text-white fs-6 fw-bold">{{ $t("utils.addRecord") }}</span>
                </router-link>

            </div>
            <div class="card-body">
                <table class="table table-striped table-hover table-responsive-md text-center">
                    <thead>
                        <tr>
                            <th>{{ $t("vehicleEntry.carrier") }}</th>
                            <th>{{ $t("vehicleEntry.driver_name") }}</th>
                            <th>{{ $t("vehicleEntry.licencePlate") }}</th>
                            <th>{{ $t("vehicleEntry.from") }}</th>
                            <th>{{ $t("vehicleEntry.to") }}</th>
                            <th>{{ $t("status.status") }}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody v-if="vehicleEntries.length">
                        <tr
                            v-for="vehicle in paginatedVehicles"
                            :key="vehicle.id"
                            class="align-middle"
                        >
                            <td>{{ vehicle.carrier }}</td>
                            <td>{{ vehicle.driver_name }}</td>
                            <td>{{ vehicle.licence_plate }}</td>
                            <td>
                                <span v-if="vehicle.actual_from">
                                    {{ $d(new Date(vehicle.actual_from), 'short') }}
                                </span>
                                <span v-else-if="vehicle.expected_from">
                                    {{ $d(new Date(vehicle.expected_from), 'short') }}
                                    <span class="fw-bold text-danger">*</span>
                                </span>
                                <span v-else>—</span>
                            </td>
                            <td>{{ vehicle.actual_to ? $d(new Date(vehicle.actual_to), 'short') : "—" }}</td>
                            <td>
                                <div
                                    v-if="$store.getters.hasGatehousePerm('change_status_vehicleentry') && vehicle.status_name !== 'left'"
                                    class="d-inline-flex align-items-center status_hover"
                                    data-bs-toggle="modal"
                                    data-bs-target="#statusModal"
                                    @click="openStatusModal(vehicle)"
                                    style="cursor: pointer;"
                                >
                                    <icon :icon="vehicle.status_icon" class="status_icon me-1" />
                                    <span>{{ $t("status.vehicleEntry." + vehicle.status_name) }}</span>
                                </div>
                                <div v-else class="d-inline-flex align-items-center status_hover">
                                    <icon :icon="vehicle.status_icon" class="status_icon me-1" />
                                    <span>{{ $t("status.vehicleEntry." + vehicle.status_name) }}</span>
                                </div>
                            </td>
                            <td>
                                <button
                                    class="btn btn-primary btn-sm me-2"
                                    @click="goToVehicleDetail(vehicle.id)"
                                >
                                    <icon icon="Search"></icon>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="7" class="text-center text-muted py-3">
                                {{ $t("vehicleEntry.notFound") }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="vehicleEntries.length >=5" class="d-flex justify-content-between mx-4">
                    <Paginate
                        :page-count="totalPages"
                        :click-handler="(p) => currentPage = p"
                        :prev-text="'«'"
                        :next-text="'»'"
                        :container-class="'pagination'"
                        :page-class="'page-item'"
                        :page-link-class="'page-link'"
                        :active-class="'active'"
                        :disabled-class="'disabled'"
                    />
                    <div class="flex-shrink-0">
                        <select v-model="perPage" class="form-select form-select-sm" style="height: 40px;">
                            <option :value="5">5</option>
                            <option :value="10">10</option>
                            <option :value="25">20</option>
                            <option :value="50">50</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <VehicleEntryStatusesModal v-if="activeVehicle" :vehicleEntry="activeVehicle" />
</template>

<script>
import Icon from '@/components/Icon.vue';
import VehicleEntryStatusesModal from '@/components/VehicleEntryStatusesModal.vue';
import Paginate from 'vuejs-paginate-next';

    export default {
        components: {
            Icon,
            VehicleEntryStatusesModal,
            Paginate,
        },
        data() {
            return {
                vehicleEntries: [],
                statuses: [],
                activeVehicle: null,
                currentPage: 1,
                perPage: 10,
            }
        },
        computed: {
            paginatedVehicles() {
                const start = (this.currentPage - 1) * this.perPage;
                return this.vehicleEntries.slice(start, start + this.perPage);
            },
            totalPages() {
                return Math.ceil(this.vehicleEntries.length / this.perPage);
            }
        },
        created() {
            this.getVehicleEntry();
            this.getStatuses();
        },
        methods: {
            getVehicleEntry() {
                this.$store.getters.api.get(`/${this.$route.params.slug}/vehicle-entries`)
                .then(response => {
                    this.vehicleEntries = response.data;
                })
                .catch(error => {
                    console.error("Error loading vehicle entries", error);
                });
            },
            getStatuses() {
                this.$store.getters.api.get(`/${this.$route.params.slug}/statuses`, {
                    params: {model: "vehicleentry"}
                })
                .then(response => {
                    this.statuses = response.data;
                })
                .catch(error => {
                    console.error("Error loading statuses", error);
                });
            },
            changeStatus(vehicle) {
                const slug = this.$route.params.slug;
                const vehicleId = vehicle.id;
                const statusId = vehicle.status;

                const url = `/${slug}/vehicle-entries/${vehicleId}/status/edit`;
                const dataPost = { status: statusId };

                this.$store.getters.api.patch(url, dataPost)
                .catch(error => {
                    console.error("Failed to update status", error)
                });
            },
            goToVehicleDetail(vehicleId) {
                this.$router.push({
                    name: "vehicleEntryDetailView",
                    params: {
                        slug: this.$route.params.slug,
                        id:vehicleId,
                    }
                });
            },
            openStatusModal(vehicle) {
                this.activeVehicle = vehicle;
            },
        },
    }
</script>

<style scoped>
.status_icon {
    width: 25px;
    height: 25px;
}

table {
    table-layout: fixed;
}
</style>